/**
 * Helpers for the admin engagement dashboard (pure; unit tested).
 */

export const REPORTING_TIMEZONE = 'Europe/London';
export const MAX_RANGE_DAYS = 366;

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function weekdayName(isoWeekday: number): string {
  return WEEKDAY_NAMES[isoWeekday - 1] ?? '';
}

/** Today's calendar date in the reporting timezone (YYYY-MM-DD). */
export function todayIn(timezone: string = REPORTING_TIMEZONE, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function daysInclusive(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

/** ISO weekday (1 = Monday) of a YYYY-MM-DD date. */
export function isoWeekday(iso: string): number {
  const day = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

export type Change = { direction: 'up' | 'down' | 'flat'; pct: number | null };

/** Change against the previous period. pct is null when the previous value is 0. */
export function compare(current: number, previous: number): Change {
  if (current === previous) return { direction: 'flat', pct: 0 };
  const direction = current > previous ? 'up' : 'down';
  if (previous === 0) return { direction, pct: null };
  return { direction, pct: Math.round(((current - previous) / previous) * 100) };
}

/** 95 → "1h 35m"; 12.4 → "12m". */
export function formatMinutes(minutes: number): string {
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** Top N items by value; the rest summed into one "Other" slice. */
export function topWithOther<T>(
  items: T[],
  value: (item: T) => number,
  label: (item: T) => string,
  n: number,
): { label: string; value: number }[] {
  const sorted = items
    .map((item) => ({ label: label(item), value: value(item) }))
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value);
  const top = sorted.slice(0, n);
  const rest = sorted.slice(n).reduce((sum, s) => sum + s.value, 0);
  return rest > 0 ? [...top, { label: 'Other', value: rest }] : top;
}

/**
 * Average estimated minutes per day of week: total minutes for sessions that
 * started on that weekday ÷ the number of those weekdays in the range.
 */
export function averageMinutesByWeekday(
  daily: { date: string; estimatedMinutes: number }[],
): { weekday: number; avgMinutes: number }[] {
  return [1, 2, 3, 4, 5, 6, 7].map((weekday) => {
    const days = daily.filter((d) => isoWeekday(d.date) === weekday);
    const total = days.reduce((sum, d) => sum + d.estimatedMinutes, 0);
    return { weekday, avgMinutes: days.length ? total / days.length : 0 };
  });
}

// ---------------------------------------------------------------------------
// Chart interaction (hover, tap and keyboard tooltips)
// ---------------------------------------------------------------------------

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2025-10-14" → "Tue 14 Oct". A calendar date, so no timezone shift. */
export function dayLabel(iso: string): string {
  const [, month, day] = iso.split('-').map(Number);
  return `${weekdayName(isoWeekday(iso))} ${day} ${MONTH_NAMES[month - 1] ?? ''}`;
}

/**
 * Index of the evenly spaced point nearest to x, for `count` points drawn from
 * `left` to `left + width` (one point sits in the middle). Null when there are none.
 */
export function nearestIndex(x: number, count: number, left: number, width: number): number | null {
  if (count <= 0) return null;
  if (count === 1 || width <= 0) return 0;
  const index = Math.round(((x - left) / width) * (count - 1));
  return Math.min(count - 1, Math.max(0, index));
}

/**
 * Next index for a navigation key over `count` items laid out in rows of
 * `columns` (one row by default). Left/Right stay in the row, Up/Down move a
 * row (or one item in a single row), Home/End go to the row ends. Null when
 * the key is not a navigation key.
 */
export function stepIndex(current: number | null, key: string, count: number, columns: number = count): number | null {
  if (count <= 0) return null;
  const last = count - 1;
  if (current === null) {
    if (key === 'End') return last;
    return ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home'].includes(key) ? 0 : null;
  }
  const rowStart = current - (current % columns);
  const rowEnd = Math.min(last, rowStart + columns - 1);
  const singleRow = columns >= count;
  switch (key) {
    case 'ArrowLeft':
      return Math.max(rowStart, current - 1);
    case 'ArrowRight':
      return Math.min(rowEnd, current + 1);
    case 'ArrowUp':
      if (singleRow) return Math.max(0, current - 1);
      return current - columns >= 0 ? current - columns : current;
    case 'ArrowDown':
      if (singleRow) return Math.min(last, current + 1);
      return current + columns <= last ? current + columns : current;
    case 'Home':
      return rowStart;
    case 'End':
      return rowEnd;
    default:
      return null;
  }
}

/**
 * Left edge for a tooltip `width` wide beside a vertical guide at `anchor`,
 * inside a box `bound` wide: to the right, flipped to the left when it would
 * overflow, and centred and clamped when neither side fits.
 */
export function placeBeside(anchor: number, width: number, bound: number, gap = 10): number {
  const right = anchor + gap;
  if (right + width <= bound) return right;
  const left = anchor - gap - width;
  if (left >= 0) return left;
  return placeCentred(anchor, width, bound);
}

/** Left edge for a tooltip `width` wide centred on `centre`, clamped inside a box `bound` wide. */
export function placeCentred(centre: number, width: number, bound: number): number {
  return Math.min(Math.max(0, bound - width), Math.max(0, centre - width / 2));
}

function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  // Neutralise spreadsheet formula injection and quote every cell.
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function toCsv(headers: string[], rows: unknown[][]): string {
  return [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}
